// AI-SLOP

import {
	ButtonGroup,
	Heading,
	IconButton,
	Layout,
	LuIcon,
	Menu,
	radiusCva,
	Text,
	useProvider,
} from "@overmap-ai/blocks"
import { type AgentProfile, type AgentUserConversation } from "@overmap-ai/common"
import { selectConversation, selectConversations, selectCurrentUser } from "@overmap-ai/core"
import useResizeObserver from "@react-hook/resize-observer"
import { Placeholder } from "@tiptap/extensions"
import { EditorContent, type EditorOptions, type JSONContent, useEditor } from "@tiptap/react"
import { cx } from "class-variance-authority"
import React, { type KeyboardEventHandler, memo, useCallback, useEffect, useMemo, useRef, useState } from "react"

import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { setActiveAgentConversationId, useAppDispatch, useAppSelector } from "../../store"
import { selectActiveAgentConversationId, selectActiveProjectId } from "../../store/selectors"
import {
	AgentResponseBubbleNode,
	BASE_EXTENSIONS,
	ChatBubble,
	DocumentLinkNode,
	JsonHiddenNode,
	MENTION_EXTENSIONS,
	OvermapNode,
} from "../../tiptap"
import { MarkdownNode } from "../../tiptap/nodes/MarkdownNode/extension"
import { getUserDisplayLabel } from "../../utils/users"
import { LoadingSteps } from "./LoadingSteps"

export const AiChatPanel = memo(() => {
	const activeProjectId = useAppSelector(selectActiveProjectId)

	if (!activeProjectId) {
		throw new Error("Active project ID is required for AiChatDialog")
	}

	const [loadingResponse, setLoadingResponse] = useState<boolean>(false)
	const [loadingSpacerHeight, setLoadingSpacerHeight] = useState<number>(0)
	const currentUser = useAppSelector(selectCurrentUser)
	const [profile, setProfile] = useState<AgentProfile | undefined | null>()
	const activeAgentConversationId = useAppSelector(selectActiveAgentConversationId)
	const dispatch = useAppDispatch()
	const { radius } = useProvider()
	const formRef = useRef<HTMLFormElement>(null)
	const scrollContainerRef = useRef<HTMLDivElement>(null)

	const conversations: AgentUserConversation[] = useAppSelector(selectConversations)

	const sortedConversations = useMemo(() => {
		return conversations.sort((a, b) => {
			if (!a.created_at) return -1
			if (!b.created_at) return 1

			return a.created_at < b.created_at ? 1 : -1
		})
	}, [conversations])

	const activeConversation: AgentUserConversation | undefined = useAppSelector(
		activeAgentConversationId ? selectConversation(activeAgentConversationId) : () => undefined,
	)

	const editor = useEditor({
		editable: false,
		extensions: [
			...BASE_EXTENSIONS,
			DocumentLinkNode,
			OvermapNode,
			ChatBubble,
			AgentResponseBubbleNode,
			MarkdownNode,
			...MENTION_EXTENSIONS,
			JsonHiddenNode,
		],
	} satisfies Partial<EditorOptions>)

	// Input editor (replaces ChatInputEditor component)
	const inputEditor = useEditor({
		editable: !loadingResponse,
		editorProps: {
			attributes: {
				class: "field-sizing-content min-h-[44px] max-h-[500px] grow resize-none bg-transparent text-base placeholder-(--base-a9) outline-none",
			},
		},
		extensions: [
			...BASE_EXTENSIONS,
			Placeholder.configure({
				placeholder: ({ editor }) => {
					if (!editor.isFocused && editor.isEmpty) return "What can I help with?"
					if (editor.isFocused) return "Type @ to mention items (Shift+Enter for newline)"
					return ""
				},
			}),
			...MENTION_EXTENSIONS,
		],
	} satisfies Partial<EditorOptions>)

	useEffect(() => {
		inputEditor?.setEditable(!loadingResponse)
	}, [inputEditor, loadingResponse])

	useEffect(() => {
		if (!editor || !activeConversation) return
		if (!activeConversation.tiptap_content?.length) {
			setLoadingResponse(true)
			void sdk.agents.fetchDetails(activeConversation.uuid).finally(() => {
				setLoadingResponse(false)
			})
		}
	}, [activeConversation, editor])

	useEffect(() => {
		if (!editor || !activeConversation?.tiptap_content?.length) return
		editor.commands.setContent({ type: "doc", content: activeConversation.tiptap_content })
	}, [activeConversation?.tiptap_content, editor])

	const insertContent = useCallback(
		(content: JSONContent | JSONContent[]) => {
			if (!editor) {
				throw new Error("tiptap is not initialized. It should not be possible to insert content yet.")
			}
			editor.chain().focus("end").insertContent(content).run()
		},
		[editor],
	)

	const scrollLatestUserBubbleToTop = useCallback(() => {
		const container = scrollContainerRef.current

		if (!container) return

		const bubbles = container.querySelectorAll<HTMLElement>(".ChatBubbleNodeWrapper")
		const latestBubble = bubbles[bubbles.length - 1]

		if (!latestBubble) return

		const containerRect = container.getBoundingClientRect()
		const bubbleRect = latestBubble.getBoundingClientRect()
		const targetTop = bubbleRect.top - containerRect.top + container.scrollTop
		container.scrollTo({ top: targetTop, behavior: "smooth" })
	}, [])

	const handlePromptSubmit: React.FormEventHandler<HTMLFormElement> = useCallback(
		(e) => {
			e.preventDefault()
			e.stopPropagation()

			if (!editor || !inputEditor) return

			const inputDoc = inputEditor.getJSON()

			// Filter out empty paragraphs from input content
			const filteredContent = (inputDoc.content ?? []).filter((node) => {
				if (node.type === "paragraph") {
					// Keep paragraph only if it has content
					return node.content && node.content.length > 0
				}
				return true
			})

			// Prepare the user's message bubble with rich content (including mentions)
			const chatBubble: JSONContent = {
				type: "ChatBubble",
				attrs: {
					created_by: currentUser?.id,
					created_at: new Date(),
				},
				content: filteredContent,
			}

			setLoadingResponse(true)

			inputEditor.commands.clearContent(true)

			insertContent(chatBubble)

			// Ensure the newly inserted user message is aligned to the top while waiting for the response
			requestAnimationFrame(() => requestAnimationFrame(scrollLatestUserBubbleToTop))

			const promptJson = JSON.stringify(chatBubble.content)

			if (activeAgentConversationId) {
				void sdk.agents
					.continueConversation(promptJson, activeAgentConversationId, activeProjectId)
					.finally(() => setLoadingResponse(false))
			} else {
				void sdk.agents
					.startConversation(promptJson, activeProjectId)
					.then((response: AgentUserConversation) => {
						dispatch(setActiveAgentConversationId(response.uuid))
					})
					.finally(() => setLoadingResponse(false))
			}
		},
		[
			editor,
			inputEditor,
			currentUser?.id,
			insertContent,
			activeAgentConversationId,
			activeProjectId,
			dispatch,
			scrollLatestUserBubbleToTop,
		],
	)

	const resetConversation = useCallback(() => {
		dispatch(setActiveAgentConversationId(null))
		setProfile(undefined)
		setLoadingResponse(false)
		editor?.chain().clearContent().run()
	}, [dispatch, editor])

	const handleNewConversation = useCallback(() => {
		resetConversation()
	}, [resetConversation])

	const handleConversationSelected = useCallback(
		(id: string | undefined) => {
			if (!id) return
			dispatch(setActiveAgentConversationId(id))
		},
		[dispatch],
	)

	const handleTextAreaPromptKeyDown: KeyboardEventHandler<HTMLDivElement> = useCallback((e) => {
		if (e.shiftKey) return

		if (e.key === "Enter") {
			formRef.current?.requestSubmit()
		}
	}, [])

	// @ts-expect-error useResizeObserver not accounting for new React 19 ref types
	useResizeObserver(scrollContainerRef, (entry, _observer) => {
		setLoadingSpacerHeight(entry.target.clientHeight)
	})

	return (
		<div className="flex size-full flex-col">
			<div className="border-(--base-5) flex items-center gap-2 border-b p-4">
				<ButtonGroup className="flex grow basis-1 gap-2" accentColor="base" variant="fill">
					<Menu.Root>
						<Menu.ClickTrigger disabled={loadingResponse}>
							<IconButton>
								<LuIcon icon={modelIconMapping.menu} />
							</IconButton>
						</Menu.ClickTrigger>
						<Menu.Content size="sm">
							<Menu.SelectGroup
								required={false}
								value={activeAgentConversationId ?? undefined}
								onValueChange={handleConversationSelected}
							>
								{sortedConversations.map((conversation) => (
									<Menu.SelectItem key={conversation.uuid} value={conversation.uuid}>
										<LuIcon icon={modelIconMapping.underline} />
										{conversation.created_at
											? new Date(conversation.created_at).toLocaleString([], {
													month: "short",
													day: "numeric",
													hour: "numeric",
													minute: "numeric",
												})
											: "Unknown conversation"}
									</Menu.SelectItem>
								))}
							</Menu.SelectGroup>
						</Menu.Content>
					</Menu.Root>

					<IconButton
						className="float-right"
						aria-label="New conversation"
						disabled={loadingResponse}
						onClick={handleNewConversation}
					>
						<LuIcon icon={modelIconMapping.add} />
					</IconButton>

					<div className="flex grow basis-1 justify-end">
						<Layout.SlideOutClose>
							<IconButton>
								<LuIcon icon={modelIconMapping.close} />
							</IconButton>
						</Layout.SlideOutClose>
					</div>
				</ButtonGroup>
			</div>

			<div
				ref={scrollContainerRef}
				className="scrollbar-gutter-stable shrink-1 relative flex size-full grow justify-center overflow-y-auto overflow-x-hidden p-4 [overflow-anchor:none]"
			>
				<div className="flex size-full max-w-3xl flex-col">
					{activeAgentConversationId || profile || loadingResponse ? (
						<EditorContent editor={editor} />
					) : (
						<div className="flex grow flex-col items-center justify-center gap-2 p-2">
							<Heading size="xl" align="center">
								Hello, {getUserDisplayLabel(currentUser)}.
							</Heading>
							<Heading size="xl" accentColor="base" align="center">
								How can I help you today?
							</Heading>
						</div>
					)}

					{loadingResponse && (
						<div className="flex w-full" style={{ minHeight: loadingSpacerHeight }}>
							<div className="mx-auto w-full max-w-xl pt-4">
								<LoadingSteps />
							</div>
						</div>
					)}
				</div>
			</div>

			<div className="flex w-full shrink-0 justify-center gap-2 px-4 pb-2 pt-1">
				<div className="flex w-full max-w-3xl flex-col gap-2">
					<form
						className={cx(
							radiusCva({ radius, maxLarge: true }),
							"bg-(--base-a2) ring-(--base-6) relative flex w-full flex-col gap-2 p-4 ring",
						)}
						ref={formRef}
						onSubmit={handlePromptSubmit}
					>
						<div className="flex h-max w-full shrink-0 grow gap-4">
							<EditorContent editor={inputEditor} onKeyDown={handleTextAreaPromptKeyDown} />
						</div>

						<div className="flex w-full items-end justify-end gap-2">
							<IconButton
								aria-label="send"
								type="submit"
								variant="surface"
								accentColor="primary"
								size="md"
								disabled={loadingResponse}
							>
								<LuIcon icon={modelIconMapping.arrowUp} />
							</IconButton>
						</div>
					</form>
					<Text size="xs" accentColor="base" weight="regular" align="center">
						The AI can make mistakes, verify the results for validity.
					</Text>
				</div>
			</div>
		</div>
	)
})
AiChatPanel.displayName = "AiChatPanel"
